// The lines the page shows, and how TfL's station names are shown.

import type {
  LineId,
  LineStyle,
  Network,
  PageLine as BasePageLine,
  RouteResponse,
  Station,
  StationId,
} from '@tol/core/types';
import type { TflPrediction, TflStopPoint, TflStopPointSequence } from '../shared/tfl.ts';

// A line as the page shows it (the core's PageLine), plus which TfL line it
// comes from when it isn't one itself (see NORTHERN_CX).
export interface PageLine extends BasePageLine {
  readonly tfl: LineId;
}

// Official colors per TfL's digital colour standard and Overground line
// announcement. `show` brightens a few that vanish on a dark map.
const LINE_TABLE: readonly (Omit<LineStyle, 'show'> & {
  readonly show?: string;
  readonly label?: string;
  readonly tfl?: LineId;
})[] = [
  { id: 'bakerloo', name: 'Bakerloo', color: '#B26300', show: '#C9761A', mode: 'tube' },
  { id: 'central', name: 'Central', color: '#DC241F', mode: 'tube' },
  { id: 'circle', name: 'Circle', color: '#FFC80A', mode: 'tube' },
  { id: 'district', name: 'District', color: '#007D32', show: '#1B9E4B', mode: 'tube' },
  { id: 'hammersmith-city', name: 'Hammersmith & City', color: '#F4A9BE', mode: 'tube' },
  { id: 'jubilee', name: 'Jubilee', color: '#A1A5A7', mode: 'tube' },
  { id: 'metropolitan', name: 'Metropolitan', color: '#9B0058', show: '#C0267A', mode: 'tube' },
  // The Northern line as the two lines TfL plans to split it into: the Bank
  // line (High Barnet-Morden, the line's black) and the Charing Cross line
  // (Edgware-Battersea), in gold. The black is drawn with a light outline
  // (render.ts CASING). Until then, trains of both run to every northern
  // end, so north of Camden Town (and south of Kennington) they share track.
  {
    id: 'northern',
    name: 'Northern: Bank',
    color: '#000000',
    mode: 'tube',
    label: 'Northern line via Bank',
  },
  {
    id: 'northern-cx',
    name: 'Northern: Charing Cross',
    color: '#F5B511',
    mode: 'tube',
    label: 'Northern line via Charing Cross',
    tfl: 'northern',
  },
  { id: 'piccadilly', name: 'Piccadilly', color: '#0019A8', show: '#4A6CFF', mode: 'tube' },
  { id: 'victoria', name: 'Victoria', color: '#0098D8', mode: 'tube' },
  { id: 'waterloo-city', name: 'Waterloo & City', color: '#93CEBA', mode: 'tube' },
  { id: 'elizabeth', name: 'Elizabeth', color: '#773DBD', show: '#9563E0', mode: 'elizabeth' },
  { id: 'dlr', name: 'DLR', color: '#00AFAD', mode: 'dlr' },
  { id: 'liberty', name: 'Liberty', color: '#61686B', show: '#8D969A', mode: 'overground' },
  { id: 'lioness', name: 'Lioness', color: '#FFA600', mode: 'overground' },
  { id: 'mildmay', name: 'Mildmay', color: '#006FE6', show: '#2C8BFF', mode: 'overground' },
  { id: 'suffragette', name: 'Suffragette', color: '#18A95D', mode: 'overground' },
  { id: 'weaver', name: 'Weaver', color: '#9B0058', show: '#B8407E', mode: 'overground' },
  { id: 'windrush', name: 'Windrush', color: '#DC241F', show: '#F0433D', mode: 'overground' },
];

export const LINES: readonly PageLine[] = LINE_TABLE.map((l) => ({
  id: l.id,
  name: l.name,
  color: l.color,
  show: l.show ?? l.color,
  mode: l.mode,
  label: l.label ?? (l.id === 'dlr' ? 'DLR' : `${l.name} line`),
  dual: l.mode === 'overground',
  tfl: l.tfl ?? l.id,
}));

// The TfL line ids behind some page lines, once each: what to ask TfL for.
export function tflIds(ids: readonly LineId[]): LineId[] {
  return [...new Set(ids.map((id) => LINE[id]?.tfl ?? id))];
}

export const NORTHERN_CX = 'northern-cx';

// Stations only one Northern branch serves, by name, and the one stretch
// between two shared stations that's only the Bank branch's (Camden Town to
// Euston direct; the Charing Cross branch goes via Mornington Crescent).
const CX_ONLY =
  /^(Mornington Crescent|Warren Street|Goodge Street|Tottenham Court Road|Leicester Square|Charing Cross|Embankment|Waterloo|Nine Elms|Battersea Power Station)\b/u;
const BANK_ONLY =
  /^(King's Cross St\.? Pancras|Angel|Old Street|Moorgate|Bank|London Bridge|Borough|Elephant (&|and) Castle)\b/u;
const bankOnlyPair = (a: string, b: string): boolean =>
  (a.startsWith('Camden Town') && b.startsWith('Euston')) || (b.startsWith('Camden Town') && a.startsWith('Euston'));
// A Northern line prediction's branch as far as it says itself: TfL's
// `towards` ("Edgware via CX", "Morden via Bank"), else the station, when
// only one branch serves it (a train due at Waterloo runs via Charing
// Cross). Null: it doesn't say ("Check Front of Train" at a shared station).
function branchSaid(p: Readonly<TflPrediction>): 'cx' | 'bank' | null {
  const towards = p.towards ?? '';
  if (/ via (cx|charing cross)\s*$/iu.test(towards)) return 'cx';
  if (/ via bank\s*$/iu.test(towards)) return 'bank';
  const station = p.stationName ?? '';
  if (CX_ONLY.test(station)) return 'cx';
  if (BANK_ONLY.test(station)) return 'bank';
  return null;
}

// The page line of each TfL prediction: Northern line trains via Charing
// Cross are the Charing Cross line's. One that doesn't say takes its
// train's (the same vehicle's other predictions), so a train's predictions
// all stay on one line; else the Bank line.
// A Northern line prediction's train, when TfL names it (not 000).
function northernVehicle(p: Readonly<TflPrediction>): string | null {
  const v = p.vehicleId ?? '';
  return p.lineId === 'northern' && v !== '' && !/^0+$/u.test(v) ? v : null;
}

export function pageLines(list: readonly Readonly<TflPrediction>[]): (string | undefined)[] {
  const byVehicle = new Map<string, 'cx' | 'bank'>();
  for (const p of list) {
    const v = northernVehicle(p);
    const said = p.lineId === 'northern' ? branchSaid(p) : null;
    // `towards` beats a station: record it over what a station said.
    if (v !== null && said !== null && (!byVehicle.has(v) || / via /iu.test(p.towards ?? ''))) byVehicle.set(v, said);
  }
  return list.map((p) => {
    if (p.lineId !== 'northern') return p.lineId;
    const v = northernVehicle(p);
    const branch = branchSaid(p) ?? (v === null ? undefined : byVehicle.get(v)) ?? 'bank';
    return branch === 'cx' ? NORTHERN_CX : 'northern';
  });
}

function branchOfEdge(a: Readonly<TflStopPoint>, b: Readonly<TflStopPoint>): 'cx' | 'bank' | 'both' {
  const [x, y] = [a.name ?? '', b.name ?? ''];
  if (CX_ONLY.test(x) || CX_ONLY.test(y)) return 'cx';
  if (BANK_ONLY.test(x) || BANK_ONLY.test(y) || bankOnlyPair(x, y)) return 'bank';
  return 'both';
}

// Runs of a sequence whose stretches belong to a branch, as sequences.
function runs(
  seq: Readonly<TflStopPointSequence>,
  keep: (e: 'cx' | 'bank' | 'both') => boolean,
): TflStopPointSequence[] {
  const pts = seq.stopPoint ?? [];
  const out: TflStopPointSequence[] = [];
  let run: TflStopPoint[] = [];
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1];
    const b = pts[i];
    if (!a || !b) continue;
    if (keep(branchOfEdge(a, b))) {
      if (run.length === 0) run.push(a);
      run.push(b);
    } else if (run.length > 0) {
      out.push({ ...seq, stopPoint: run });
      run = [];
    }
  }
  if (run.length > 0) out.push({ ...seq, stopPoint: run });
  return out;
}

// TfL's Northern line routes, cut into the Bank line's and the Charing
// Cross line's. Other routes pass through.
export function splitRoutes(responses: readonly RouteResponse[]): RouteResponse[] {
  const out: RouteResponse[] = [];
  for (const r of responses) {
    if (r.lineId !== 'northern') {
      out.push(r);
      continue;
    }
    const part = (keep: (e: 'cx' | 'bank' | 'both') => boolean): RouteResponse['data'] => ({
      ...r.data,
      stopPointSequences: r.data.stopPointSequences.flatMap((seq) => runs(seq, keep)),
    });
    out.push({ ...r, data: part((e) => e !== 'cx') }, { ...r, lineId: NORTHERN_CX, data: part((e) => e !== 'bank') });
  }
  return out;
}

export const LINE: Readonly<Partial<Record<LineId, PageLine>>> = Object.fromEntries(LINES.map((l) => [l.id, l]));

export function lineStyle(id: LineId): PageLine {
  const l = LINE[id];
  if (!l) throw new TypeError(`no style for line ${id}`);
  return l;
}

// The lines polled together for arrivals, one request per group.
export const ARRIVAL_GROUPS: readonly (readonly LineId[])[] = [
  LINES.filter((l) => l.mode === 'tube').map((l) => l.id),
  ['elizabeth', 'dlr'],
  LINES.filter((l) => l.mode === 'overground').map((l) => l.id),
];

const strip = (n: string): string =>
  n
    .replace(/-Underground$/iu, '')
    .replaceAll(/\s*\([^)]*\)/gu, '')
    .trim();

// TfL's names to what people say: "Stratford (London)" -> "Stratford",
// "Paddington (H&C Line)-Underground" -> "Paddington", "London Euston" ->
// "Euston". The "London" prefix only goes when the rest is another
// station's name, so London Bridge and London Fields stay put. Returns the
// network with its stations renamed.
export function tidyNames(net: Network): Network {
  const names = new Set([...net.stations.values()].map((st) => strip(st.name)));
  const stations = new Map<StationId, Station>();
  for (const [id, st] of net.stations) {
    let n = strip(st.name);
    const m = /^London (.+)$/u.exec(n);
    if (m?.[1] !== undefined && names.has(m[1])) n = m[1];
    stations.set(id, { ...st, name: n });
  }
  return { ...net, stations };
}
